/* eslint-disable class-methods-use-this */
import React from 'react';
import BaseClass from 'components/BaseClass';
import { wrapComponent } from 'utils/framework';
import NewUserPaymentView from 'components/FrictionlessCheckout/PaymentReleaseFour/NewUserPaymentView';
import processEvent from 'analytics/processEvent';
import OrderUtils from 'utils/Order';
import anaConsts from 'analytics/constants';
import analyticsUtils from 'analytics/utils';
import { KlarnaExpandedView } from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Klarna';
import { AfterPayExpandedView } from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/AfterPay';
import { VenmoExpandedView } from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Venmo';
import PazeExpandedView from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Paze/PazeExpandedView';
import Debounce from 'utils/Debounce';
import checkoutUtils from 'utils/Checkout';
import ApplePay from 'services/ApplePay';
import { Box, Icon, Divider } from 'components/ui';
import CreditCardArea from 'components/FrictionlessCheckout/PaymentReleaseFour/CreditCard/CreditCardArea';
import UseAGiftCard from 'components/FrictionlessCheckout/PaymentReleaseFour/GiftCards/UseAGiftCard';
import AppliedGiftCards from 'components/FrictionlessCheckout/PaymentReleaseFour/GiftCards/AppliedGiftCards';
import AddCreditCardButton from 'components/FrictionlessCheckout/PaymentReleaseFour/CreditCard/AddCreditCardButton';
import CreditCardsList from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/CreditCardsList';
import creditCardUtils from 'utils/CreditCard';
import deepExtend from 'utils/deepExtend';
import checkoutBindings from 'analytics/bindings/pages/checkout/checkoutBindings';
import ErrorsUtils from 'utils/Errors';
import ErrorMsg from 'components/ErrorMsg';
import AccountCredit from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/AccountCredit';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import { PAYMENT_TYPES } from 'constants/RwdBasket';
import { screenReaderOnlyStyle } from 'style/config';
import { SECTION_NAMES } from 'constants/frictionlessCheckout';
import { createKlarnaWidgetLoader } from 'components/FrictionlessCheckout/PaymentReleaseFour/utils/AlternatePaymentWidgetHelpers';
import Venmo from 'utils/Venmo';
import PayPal from 'utils/PayPal';
import Location from 'utils/Location';
import { refreshCheckout } from 'services/Checkout';

const { PAYMENT_FLAGS } = OrderUtils;

class PaymentMethodList extends BaseClass {
    state = {
        openCVCModal: false,
        isPayWithPayPal: this.props.isPayPalSelected,
        shouldShowPaze: false,
        openCreditCardForm: this.props.openCreditCardForm,
        isApplePayEnabled: false,
        isApplePayFlow: false,
        isPayWithApplePay: false,
        selectedCreditCardId: null,
        isAfterPayExpanded: false,
        afterpayApiData: null,
        isAfterPayLoading: false,
        afterpayError: null,
        isKlarnaExpanded: false,
        klarnaApiData: null,
        isKlarnaLoading: false,
        klarnaError: null,
        isPazeExpanded: false,
        useMyShippingAddress: true,
        isVenmoExpanded: false
    };

    /* eslint-disable-next-line complexity */
    componentDidMount() {
        const {
            creditCardPaymentGroup, defaultPayment, isPayPalSelected, paymentName, hasPendingCreditCard, alternateMethodSelected
        } = this.props;

        if ((!creditCardPaymentGroup.isComplete && !hasPendingCreditCard && this.state.selectedCreditCardId) || paymentName) {
            this.newCreditCard = {};
            this.setState({
                selectedCreditCardId: null,
                securityCode: ''
            });
        } else if ((hasPendingCreditCard || creditCardPaymentGroup.isComplete) && !this.state.isPayWithApplePay && !alternateMethodSelected) {
            // Only set CC if no alternate payment selected (prevents resetting Klarna/Afterpay)
            if (!this.state.selectedCreditCardId || this.state.selectedCreditCardId === creditCardPaymentGroup.creditCardId) {
                this.setNewCreditCard(creditCardPaymentGroup, defaultPayment);
            }
        }

        //check if payment section is complete (if terms and conditions have been checked)
        //if not, then if paypal was updated, select paypal radio button or
        //if credit card form is open, close the form
        if (!checkoutUtils.isPaymentSectionComplete()) {
            if (isPayPalSelected && !this.state.isPayWithPayPal) {
                this.switchPaymentState(PAYMENT_FLAGS.isPayWithPayPal);
            }
        }

        // Auto-expand AfterPay if triggered from L1 click
        if (this.props.shouldAutoExpandAfterpay) {
            this.handleAfterPayExpand();
            this.props.clearAutoExpandAfterpay && this.props.clearAutoExpandAfterpay();
        }

        // Auto-expand Klarna if triggered from L1 click
        if (this.props.shouldAutoExpandKlarna) {
            this.handleKlarnaExpand();
            this.props.clearAutoExpandKlarna && this.props.clearAutoExpandKlarna();
        }

        // Auto-expand Paze if triggered from L1 click
        if (this.props.shouldAutoExpandPaze) {
            this.handlePazeExpand();
            this.props.clearAutoExpandPaze && this.props.clearAutoExpandPaze();
        }

        // Auto-expand PayPal if triggered from L1 click
        if (this.props.shouldAutoExpandPayPal) {
            this.handlePayPalPillClick();
            this.props.clearAutoExpandPayPal && this.props.clearAutoExpandPayPal();
        }
    }

    componentDidUpdate(prevProps) {
        const shouldOpenForm = this.props.openCreditCardForm && !this.state.openCreditCardForm;

        if (prevProps.openCreditCardForm !== this.props.openCreditCardForm && shouldOpenForm) {
            this.setState({ openCreditCardForm: true });
        }

        // Autoexpand AfterPay if flag was set after initial mount
        if (this.props.shouldAutoExpandAfterpay && !prevProps.shouldAutoExpandAfterpay) {
            this.handleAfterPayExpand();
            this.props.clearAutoExpandAfterpay && this.props.clearAutoExpandAfterpay();
        }

        // Auto expand Klarna if flag was set after initial mount
        if (this.props.shouldAutoExpandKlarna && !prevProps.shouldAutoExpandKlarna) {
            this.handleKlarnaExpand();
            this.props.clearAutoExpandKlarna && this.props.clearAutoExpandKlarna();
        }

        // Auto expand Paze if flag was set after initial mount
        if (this.props.shouldAutoExpandPaze && !prevProps.shouldAutoExpandPaze) {
            this.handlePazeExpand();
            this.props.clearAutoExpandPaze && this.props.clearAutoExpandPaze();
        }

        // Auto expand PayPal if flag was set after initial mount
        if (this.props.shouldAutoExpandPayPal && !prevProps.shouldAutoExpandPayPal) {
            this.handlePayPalPillClick();
            this.props.clearAutoExpandPayPal && this.props.clearAutoExpandPayPal();
        }
    }

    toggleOpenCreditCardForm =
        (closeHandler = false) =>
            e => {
                e?.preventDefault();
                const { paymentOptions, locales } = this.props;
                const { removeAddress, maxCreditCardsMessage, continueButton } = locales;
                const maxCreditCards = Sephora.configurationSettings.maxCreditCards;
                const numberOfCreditCards = paymentOptions?.creditCards?.length;

                if (numberOfCreditCards === maxCreditCards && !closeHandler) {
                    const title = removeAddress;
                    const message = maxCreditCardsMessage;
                    const confirmButtonText = continueButton;

                    this.props.actionModal(title, message, confirmButtonText);
                } else {
                    this.setState(
                        prevState => ({
                            openCreditCardForm: !prevState.openCreditCardForm,
                            isEditCCFlow: false
                        }),
                        () => {
                            if (this.state?.openCreditCardForm) {
                                const pageDetailPrefix = this.props.isPhysicalGiftCardOnly ? 'gift card ' : '';
                                checkoutBindings.processAsyncPageLoad(pageDetailPrefix + 'payment-add credit card');
                            }
                        }
                    );
                }
            };

    handlePayWithApplePayClick = Debounce.preventDoubleClick(() => {
        this.switchPaymentState(PAYMENT_FLAGS.isPayWithApplePay);
        this.props.swapPaypalToCreditCard();

        if (checkoutUtils.isGuestOrder()) {
            ApplePay.enableGuestCheckout();
        }

        this.processPaymentClickEvent('checkout:payment:applepay');
        ApplePay.onApplePayClicked();
    });

    // Handles PayPal pill click in L2. Uses existing PayPal modal + interstice flow.
    handlePayPalPillClick = Debounce.preventDoubleClick(() => {
        const { paymentOptions, locales } = this.props;

        if (OrderUtils.containsRestrictedItem()) {
            this.props.setCheckoutSectionErrors({ paymentMethod: locales.paypalRestrictedItemError });

            return;
        }

        // If no PayPal account is saved, open the PayPal modal and persist the tokenized payload.
        if (!paymentOptions?.paypal) {
            PayPal.showPayPal(payload => {
                this.props.payPalWithInterstice(payload).then(() => {
                    this.props.commonOrderToggleActions();
                    this.props.updatePaymentActions();
                    this.props.sectionSaveOrderAction(Location.getLocation().pathname, this);
                    refreshCheckout()().then(() => {
                        this.props.setEditChangeLink(false);
                        this.props.saveBtnSuccessCallback?.();
                    });

                    if (checkoutUtils.isGuestOrder()) {
                        this.switchPaymentState(PAYMENT_FLAGS.isPayWithPayPal);
                    }
                });
            });
        } else {
            // PayPal already saved on the order, just switch payment state and return to L1.
            this.switchPaymentState(PAYMENT_FLAGS.isPayWithPayPal);

            if (checkoutUtils.isGuestOrder()) {
                checkoutUtils.disablePlaceOrderButtonBasedOnCheckoutCompleteness();
            }

            this.props.setEditChangeLink(false);
            this.props.saveBtnSuccessCallback?.();
        }

        this.processPaymentClickEvent('checkout:express-payment:paypal');
    });

    handlePaymentClick = paymentType =>
        Debounce.preventDoubleClick(() => {
            this.switchPaymentState(paymentType);
            const paymentName = OrderUtils.getPaymentNameByType(paymentType);
            this.processPaymentClickEvent(`checkout:payment:${paymentName}`);
        });

    switchPaymentState = selectedPayment => {
        const newState = { selectedCreditCardId: null };
        Object.keys(PAYMENT_FLAGS).forEach(x => (newState[x] = selectedPayment === PAYMENT_FLAGS[x]));
        this.setState(newState, () => this.updatePayment(selectedPayment));

        processEvent.process(anaConsts.ADD_PAYMENTINFO_EVENT, {
            data: {
                paymentType: selectedPayment
            }
        });
    };

    updatePayment = selectedPayment => {
        const { updatePaymentActions, commonOrderToggleActions } = this.props;

        commonOrderToggleActions(
            selectedPayment === PAYMENT_FLAGS.isPayWithApplePay,
            selectedPayment === PAYMENT_FLAGS.isPayWithKlarna,
            selectedPayment === PAYMENT_FLAGS.isPayWithAfterpay,
            selectedPayment === PAYMENT_FLAGS.isPayWithVenmo
        );

        updatePaymentActions(selectedPayment === PAYMENT_FLAGS.isPayWithPaze, selectedPayment === PAYMENT_FLAGS.isPayWithApplePay);
    };

    setNewCreditCard = (creditCardData, defaultPayment) => {
        const {
            utilMerge, klarnaToggle, afterPayToggle, pazeToggle, venmoToggle, afterPayResetError
        } = this.props;
        const creditCardIsDefaultPayment = !defaultPayment || defaultPayment === 'creditCard';
        this.newCreditCard = {
            creditCard: creditCardUtils.cleanCreditCardData(creditCardData),
            isMarkAsDefault: creditCardIsDefaultPayment && creditCardData.isDefault
        };

        utilMerge('order', 'isApplePayFlow', false);
        klarnaToggle(false);
        afterPayToggle(false);
        pazeToggle(false);
        venmoToggle(false);
        afterPayResetError();

        this.setState({
            selectedCreditCardId: creditCardData.creditCardId,
            isPayWithPayPal: false,
            isPayWithApplePay: false,
            securityCode: ''
        });
    };

    processPaymentClickEvent = actionInfo => {
        const eventData = {
            eventStrings: [anaConsts.Event.EVENT_71],
            linkName: 'D=c55',
            actionInfo: actionInfo
        };

        const mostRecentAsyncLoadEvent = analyticsUtils.getMostRecentEvent('asyncPageLoad');

        if (mostRecentAsyncLoadEvent) {
            eventData.previousPage = mostRecentAsyncLoadEvent.eventInfo.attributes.previousPageName;
        }

        processEvent.process(anaConsts.LINK_TRACKING_EVENT, { data: eventData });
    };

    /**
     * Triggered when user clicks the Afterpay pill button.
     * Opens the AfterPayExpandedView component inline.
     * Widget loading is handled by AfterPayExpandedView after mount.
     *
     * @async
     */
    handleAfterPayExpand = async () => {
        const widgetElementId = 'afterpay-widget-expanded-view-pml';

        // Set expanded state with loading indicator and the widget element ID
        // The element ID is set immediately so the DOM element renders
        // IMPORTANT: Clear other expanded states to ensure only one is visible at a time
        this.setState({
            isAfterPayExpanded: true,
            isAfterPayLoading: true,
            afterpayError: null,
            afterpayApiData: { widgetElementId },
            // Clear other expanded states
            isKlarnaExpanded: false,
            isVenmoExpanded: false,
            isPazeExpanded: false
        });

        // track expansion and async page load for L3 state
        FrictionlessCheckoutBindings.setAfterpayExpandedViewAnalytics();
        this.processPaymentClickEvent('checkout:payment:afterpay:expand');
    };

    /**
     * Callback for AfterPayExpandedView to load the widget after mount.
     * This ensures the DOM element exists before calling the SDK.
     */
    loadAfterPayWidget = async () => {
        const { orderDetails, loadExpandedViewWidget } = this.props;
        const priceInfo = orderDetails?.priceInfo;
        // Try multiple fields to get the order amount bc the SDK requires a positive amount
        const orderTotal = priceInfo?.creditCardAmount || priceInfo?.paypalAmount || priceInfo?.orderTotal || 0;
        const widgetElementId = 'afterpay-widget-expanded-view-pml';

        // Afterpay SDK requires a positive amount, show error if amount is 0 or invalid
        if (!orderTotal || orderTotal === '$0.00' || orderTotal === '0') {
            this.setState({
                isAfterPayLoading: false,
                afterpayError: 'Unable to calculate payment schedule. Please try again.'
            });

            return;
        }

        if (loadExpandedViewWidget) {
            // Set a timeout fallback in case SDK never calls onReady
            const timeoutId = setTimeout(() => {
                this.setState({ isAfterPayLoading: false });
            }, 10000);

            try {
                await loadExpandedViewWidget(widgetElementId, orderTotal);
                clearTimeout(timeoutId);
                this.setState({ isAfterPayLoading: false });
            } catch (error) {
                clearTimeout(timeoutId);
                this.setState({
                    isAfterPayLoading: false,
                    afterpayError: error?.message || 'Unable to load Afterpay information. Please try again.'
                });
            }
        } else {
            this.setState({ isAfterPayLoading: false });
        }
    };

    /**
     * Triggered when user clicks "Save and Continue" button in AfterPayExpandedView.
     * Collapses the L3 expanded view, selects Afterpay as payment method, and returns to L1 state
     * Sets Afterpay as ready so PlaceOrderButton shows "Continue with Afterpay"
     */
    handleAfterPaySaveAndContinue = () => {
        // Collapse expanded view
        this.setState({ isAfterPayExpanded: false });

        // Select Afterpay as payment method (sets Redux state)
        // Call commonOrderToggleActions directly instead of through switchPaymentState
        // because switchPaymentState uses a setState callback which creates a race condition
        // with the navigation below. The Redux state must be updated synchronously before navigating.
        this.props.commonOrderToggleActions(false, false, true, false);
        this.props.updatePaymentActions(false, false);
        this.props.afterpaySetReady && this.props.afterpaySetReady(true);
        this.props.clearPendingCreditCard && this.props.clearPendingCreditCard();

        // Navigate back to L1 state (close edit mode)
        this.props.setEditChangeLink(false);
        this.props.saveBtnSuccessCallback();

        // Analytics - track user saving Afterpay selection
        this.processPaymentClickEvent('checkout:payment:afterpay:save');
    };

    /**
     * Returns user to L2 edit mode.
     */
    handleAfterPayCancel = () => {
        // Collapse expanded view and clear all Afterpay data
        this.setState({
            isAfterPayExpanded: false,
            afterpayApiData: null, // Clear widget data
            afterpayError: null // Clear any errors
        });

        // Clear Afterpay ready state when canceled
        this.props.afterpaySetReady(false);

        // Analytics - track user canceling Afterpay flow
        this.processPaymentClickEvent('checkout:payment:afterpay:cancel');
    };

    /**
     * Triggered when user clicks the Klarna pill button.
     * Opens the KlarnaExpandedView component inline.
     */
    handleKlarnaExpand = async () => {
        const widgetElementId = 'klarna-widget-expanded-view-pml';

        // IMPORTANT: Clear other expanded states to ensure only one is visible at a time
        this.setState({
            isKlarnaExpanded: true,
            isKlarnaLoading: true,
            klarnaError: null,
            klarnaApiData: { widgetElementId },
            // Clear other expanded states
            isAfterPayExpanded: false,
            isVenmoExpanded: false,
            isPazeExpanded: false
        });

        FrictionlessCheckoutBindings.setKlarnaExpandedViewAnalytics && FrictionlessCheckoutBindings.setKlarnaExpandedViewAnalytics();
        this.processPaymentClickEvent('checkout:payment:klarna:expand');
    };

    /**
     * Loads Klarna SDK widget after DOM element is ready.
     * Uses shared Util to keep code dry.
     */
    loadKlarnaWidget = async () => {
        const loader = createKlarnaWidgetLoader({
            widgetElementId: 'klarna-widget-expanded-view-pml',
            setState: this.setState.bind(this),
            klarnaSetReady: this.props.klarnaSetReady,
            locales: this.props.locales
        });

        return loader();
    };

    /**
     * Triggered when user clicks "Save and Continue" button in KlarnaExpandedView.
     * Collapses the L3 expanded view, selects Klarna as payment method, and returns to L1 state.
     * Sets Klarna as ready so PlaceOrderButton shows "Continue with Klarna"
     */
    handleKlarnaSaveAndContinue = () => {
        this.setState({ isKlarnaExpanded: false });
        // Call commonOrderToggleActions directly instead of through switchPaymentState
        // because switchPaymentState uses a setState callback which creates a race condition
        // with the navigation below. The Redux state must be updated synchronously before navigating.
        this.props.commonOrderToggleActions(false, true, false, false); // klarna = true
        this.props.updatePaymentActions(false, false);
        this.props.klarnaSetReady && this.props.klarnaSetReady(true);
        this.props.clearPendingCreditCard && this.props.clearPendingCreditCard();

        // Navigate back to L1 state (close edit mode)
        this.props.setEditChangeLink(false);
        this.props.saveBtnSuccessCallback();

        this.processPaymentClickEvent('checkout:payment:klarna:save');
    };

    /**
     * Triggered when user clicks "Cancel" button in KlarnaExpandedView.
     * Collapses the L3 expanded view without selecting Klarna.
     * Returns user to L2 edit mode.
     */
    handleKlarnaCancel = () => {
        this.setState({
            isKlarnaExpanded: false,
            klarnaApiData: null,
            klarnaError: null
        });

        // Clear Klarna ready state when canceled
        this.props.klarnaSetReady && this.props.klarnaSetReady(false);

        this.processPaymentClickEvent('checkout:payment:klarna:cancel');
    };

    /**
     * Triggered when user clicks the Venmo pill button.
     * Opens the Venmo expanded view inline.
     */
    handleVenmoExpand = () => {
        Venmo.prepareVenmoCheckout();
        // IMPORTANT: Clear other expanded states to ensure only one is visible at a time
        this.setState({
            isVenmoExpanded: true,
            // Clear other expanded states
            isKlarnaExpanded: false,
            isAfterPayExpanded: false,
            isPazeExpanded: false
        });
        this.processPaymentClickEvent('checkout:payment:venmo:expand');
    };

    /**
     * Triggered when user clicks the Paze pill button.
     * Opens the Paze expanded view inline.
     */
    handlePazeExpand = () => {
        this.setState({
            isPazeExpanded: true,
            isKlarnaExpanded: false,
            isAfterPayExpanded: false,
            isVenmoExpanded: false
        });
        this.processPaymentClickEvent('checkout:payment:paze:expand');
    };

    /**
     * Triggered when user clicks "Save and Continue" in Paze expanded view.
     * Selects Paze and returns to L1 state.
     */
    handlePazeSaveAndContinue = () => {
        this.setState({ isPazeExpanded: false });
        this.props.commonOrderToggleActions(false, false, false, false);
        this.props.updatePaymentActions(true, false);
        this.props.pazeSetReady && this.props.pazeSetReady(true);
        this.props.clearPendingCreditCard && this.props.clearPendingCreditCard();
        this.props.setEditChangeLink(false);
        this.props.saveBtnSuccessCallback && this.props.saveBtnSuccessCallback();
        this.processPaymentClickEvent('checkout:payment:paze:save');
    };

    /**
     * Triggered when user clicks "Cancel" in Paze expanded view.
     * Collapses the L3 expanded view without selecting Paze.
     */
    handlePazeCancel = () => {
        this.setState({ isPazeExpanded: false });
        this.props.pazeSetReady && this.props.pazeSetReady(false);
        this.processPaymentClickEvent('checkout:payment:paze:cancel');
    };

    /**
     * Triggered when user clicks "Save and Continue" in Venmo expanded view.
     * Selects Venmo and returns to L1 state.
     */
    handleVenmoSaveAndContinue = () => {
        this.setState({ isVenmoExpanded: false });
        this.switchPaymentState(PAYMENT_FLAGS.isPayWithVenmo);
        this.props.clearPendingCreditCard && this.props.clearPendingCreditCard();
        this.props.setEditChangeLink(false);
        this.props.saveBtnSuccessCallback && this.props.saveBtnSuccessCallback();
        this.processPaymentClickEvent('checkout:payment:venmo:save');
    };

    /**
     * Triggered when user clicks "Cancel" in Venmo expanded view.
     * Collapses the L3 expanded view without selecting Venmo.
     * Returns user to L2 edit mode.
     */
    handleVenmoCancel = () => {
        this.setState({ isVenmoExpanded: false });
        this.processPaymentClickEvent('checkout:payment:venmo:cancel');
    };

    /**
     * Toggles billing address checkbox
     */
    handleBillingAddressToggle = () => {
        this.setState(prevState => ({
            useMyShippingAddress: !prevState.useMyShippingAddress
        }));
    };

    showEditCreditCardForm = prevCreditCardInfo => {
        const creditCardInfo = deepExtend({}, prevCreditCardInfo);

        //format exp month for edit credit card
        if (creditCardInfo.expirationMonth.length === 1) {
            creditCardInfo.expirationMonth = creditCardUtils.formatExpMonth(creditCardInfo.expirationMonth);
        }

        //format exp year for edit credit card
        if (creditCardInfo.expirationYear.length === 4) {
            creditCardInfo.expirationYear = creditCardUtils.formatExpYear(creditCardInfo.expirationYear);
        }

        creditCardInfo.expirationDate = `${creditCardInfo.expirationMonth}/${creditCardInfo.expirationYear}`;

        this.setState(
            {
                openCreditCardForm: true,
                editCreditCard: creditCardInfo,
                isEditCCFlow: true,
                isUseShipAddressAsBilling: creditCardUtils.isShipAddressBillingAddress(this.props.shippingAddress, creditCardInfo.address)
            },
            () => {
                //Analytics
                const pageDetailPrefix = this.props.isPhysicalGiftCardOnly ? 'gift card ' : '';
                checkoutBindings.processAsyncPageLoad(pageDetailPrefix + 'payment-edit credit card');
            }
        );
    };

    updateSecurityCode = securityCode => {
        this.setState({ securityCode: securityCode });
    };

    saveAndContinue = e => {
        e.preventDefault();
        ErrorsUtils.clearErrors();
        const componentsToValidate = [];

        if (this.creditCardsList?.refs?.securityCodeInput) {
            componentsToValidate.push(this.creditCardsList.refs.securityCodeInput);
        }

        ErrorsUtils.collectClientFieldErrors(componentsToValidate);

        this.props.setPaymentMethodNeedsConfirmation(false);

        if (!ErrorsUtils.validate()) {
            // Handle the case when PayPal is selected
            if (this.state.isPayWithPayPal) {
                this.props.setEditChangeLink(false);
            }

            // Set security code on credit card if present
            if (this.state.securityCode) {
                this.newCreditCard.creditCard.securityCode = this.state.securityCode;
            }

            // Handle the case when a credit card is selected
            if (this.state.selectedCreditCardId) {
                if (this.newCreditCard) {
                    // delete property as it is not required for updateCreditCardOnOrder API
                    delete this.newCreditCard.creditCard?.isCVVValidationRequired;
                }

                const creditCardToSave = this.props.paymentOptions.creditCards.filter(
                    creditCard => creditCard.creditCardId === this.newCreditCard.creditCard.creditCardId
                )?.[0];

                // Save pending credit card to Redux instead of making API call
                this.props.savePendingCreditCard(creditCardToSave);
                this.props.commonOrderToggleActions();

                // Close form and show success (skip API call)
                this.props.setEditChangeLink(false);

                if (this.props.saveBtnSuccessCallback) {
                    this.props.saveBtnSuccessCallback();
                }
            } else if (!this.state.isPayWithPayPal) {
                // Handle the case when no credit card or PayPal is selected
                // This happens when a gift card covers the entire order amount
                // Close the edit mode, accepting the gift card as the only payment method

                if (this.props.sectionErrors?.Payment) {
                    FrictionlessCheckoutBindings.setSectionLevelErrorAnalytics(SECTION_NAMES.PAYMENT, this.props.sectionErrors?.Payment);
                    this.props.setCheckoutSectionErrors(this.props.sectionErrors);
                } else if (this.props.saveBtnSuccessCallback) {
                    this.props.setEditChangeLink(false);
                    this.props.saveBtnSuccessCallback();
                }
            }
        }
    };

    saveAndContinueDebounce = Debounce.preventDoubleClick(this.saveAndContinue);

    renderGiftCard = () => {
        const {
            showGiftCard,
            alternateMethodSelected,
            isGuestCheckout,
            alternatePaymentName,
            zeroDollarHigherRedeemPointsCVV,
            hasGiftCardInOrder,
            isPhysicalGiftCardOnly
        } = this.props;

        const shouldRenderGiftCard = (showGiftCard || (isGuestCheckout && !isPhysicalGiftCardOnly)) && !alternateMethodSelected;
        const isVenmoSelected = alternatePaymentName === PAYMENT_TYPES.PAY_VENMO;

        if ((shouldRenderGiftCard || isVenmoSelected) && !zeroDollarHigherRedeemPointsCVV && hasGiftCardInOrder) {
            return (
                <Box marginTop={2}>
                    <UseAGiftCard isEditMode />
                </Box>
            );
        }

        return null;
    };

    render() {
        const {
            paymentOptions,
            isKlarnaEnabledForThisOrder,
            isVenmoEnabledForThisOrder,
            isAfterpayEnabledForThisOrder,
            isAfterpayEnabledForThisProfile,
            isPazeEnabledForThisOrder,
            isPayPalEnabled,
            isPayPalPayLaterEligible,
            shouldDisplayAddOrDeleteCreditCardButton,
            orderDetails,
            isZeroCheckout,
            isBopis,
            creditCardPaymentGroup,
            showGCAmountNotEnoughMessage,
            isCreditCardRequiredMessage,
            zeroDollarHigherRedeemPointsCVV,
            storeCredits,
            isPaymentComplete,
            locales,
            paymentLocales,
            installmentValue
        } = this.props;

        const { openCreditCardForm, securityCodeInvalid, securityCode } = this.state;

        const storeCreditNotEnough = storeCredits?.length > 0 && !isPaymentComplete;
        const isCreditCardRequiredError = isCreditCardRequiredMessage?.length > 0;

        // L3 State: Venmo Expanded View
        // When Venmo is expanded, show ONLY the Venmo payment details
        if (this.state.isVenmoExpanded) {
            return (
                <>
                    {/* Hidden container for legacy Klarna background initialization */}
                    <div
                        id='backgroundPaymentWidget'
                        style={{ display: 'none' }}
                    />
                    <VenmoExpandedView
                        venmoHeader={locales.venmoHeader}
                        venmoSaveAndContinue={locales.venmoSaveAndContinue}
                        venmoCancel={locales.cancel}
                        onSaveAndContinue={this.handleVenmoSaveAndContinue}
                        onCancel={this.handleVenmoCancel}
                        isAnonymous={checkoutUtils.isGuestOrder()}
                    />
                </>
            );
        }

        // L3 State: Paze Expanded View
        // When Paze is expanded, show ONLY the Paze payment details
        if (this.state.isPazeExpanded) {
            return (
                <PazeExpandedView
                    pazeHeader={locales.pazeHeader}
                    pazeSubtitle={locales.pazeSubtitle}
                    pazeGiftCardWarning={locales.pazeGiftCardWarning}
                    pazeSaveAndContinue={locales.pazeSaveAndContinue}
                    pazeCancel={locales.pazeCancel}
                    onSaveAndContinue={this.handlePazeSaveAndContinue}
                    onCancel={this.handlePazeCancel}
                />
            );
        }

        // L3 State: Klarna Expanded View
        // When Klarna is expanded, show ONLY the Klarna payment details
        if (this.state.isKlarnaExpanded) {
            return (
                <>
                    {/* Hidden container for legacy Klarna background initialization */}
                    <div
                        id='backgroundPaymentWidget'
                        style={{ display: 'none' }}
                    />
                    <KlarnaExpandedView
                        klarnaApiData={this.state.klarnaApiData}
                        onSaveAndContinue={this.handleKlarnaSaveAndContinue}
                        onCancel={this.handleKlarnaCancel}
                        loadWidget={this.loadKlarnaWidget}
                        klarnaHeader={locales.klarnaHeader}
                        klarnaSubtitle={locales.klarnaSubtitle}
                        klarnaSaveAndContinue={locales.klarnaSaveAndContinue}
                        klarnaCancel={locales.klarnaCancel}
                        klarnaLoading={locales.klarnaLoading}
                        klarnaApiErrorFallback={locales.klarnaApiErrorFallback}
                        klarnaGiftCardWarning={locales.klarnaGiftCardWarning}
                        klarnaFeatureBuyerProtection={locales.klarnaFeatureBuyerProtection}
                        klarnaTermsText={locales.klarnaTermsText}
                        klarnaLegalNotice={locales.klarnaLegalNotice}
                        klarnaBillingAddressCheckbox={locales.klarnaBillingAddressCheckbox}
                        isLoading={this.state.isKlarnaLoading}
                        errorMessage={this.state.klarnaError}
                        installmentValue={installmentValue}
                        isAnonymous={checkoutUtils.isGuestOrder()}
                        isBopisOrder={orderDetails?.header?.isBopisOrder}
                        useMyShippingAddress={this.state.useMyShippingAddress}
                        onBillingAddressToggle={this.handleBillingAddressToggle}
                    />
                </>
            );
        }

        // L3 State: AfterPay Expanded View
        // When AfterPay is expanded, show ONLY the AfterPay payment details
        if (this.state.isAfterPayExpanded) {
            return (
                <>
                    {/* Hidden container for legacy Klarna background initialization */}
                    <div
                        id='backgroundPaymentWidget'
                        style={{ display: 'none' }}
                    />
                    <AfterPayExpandedView
                        afterpayApiData={this.state.afterpayApiData}
                        onSaveAndContinue={this.handleAfterPaySaveAndContinue}
                        onCancel={this.handleAfterPayCancel}
                        loadWidget={this.loadAfterPayWidget}
                        afterpayHeader={locales.afterpayHeader}
                        afterpaySaveAndContinue={locales.afterpaySaveAndContinue}
                        afterpayCancel={locales.afterpayCancel}
                        afterpayLoading={locales.afterpayLoading}
                        afterpayApiErrorFallback={locales.afterpayApiErrorFallback}
                        afterpayGiftCardWarning={locales.afterpayGiftCardWarning}
                        afterpayLegalNotice={locales.afterpayLegalNotice}
                        isLoading={this.state.isAfterPayLoading}
                        errorMessage={this.state.afterpayError}
                        installmentValue={installmentValue}
                        isAnonymous={checkoutUtils.isGuestOrder()}
                        isBopisOrder={orderDetails?.header?.isBopisOrder}
                    />
                </>
            );
        }

        return (
            <>
                {/* Hidden container for legacy Klarna background initialization (old modal flow) */}
                {/* This prevents "InvalidContainerSelectorError" when commonOrderToggleActions dispatches backgroundInit */}
                <div
                    id='backgroundPaymentWidget'
                    style={{ display: 'none' }}
                />

                {openCreditCardForm && (
                    <CreditCardArea
                        handleCloseForm={this.toggleOpenCreditCardForm(true)}
                        callback={this.props.setEditChangeLink}
                        editCreditCard={this.state.editCreditCard}
                        isEditCCFlow={this.state.isEditCCFlow}
                        isGuestCheckout={this.props.isGuestCheckout}
                        hideCancelButton={this.props.openCreditCardForm}
                    />
                )}
                {!openCreditCardForm && (
                    <>
                        {showGCAmountNotEnoughMessage ||
                            ((storeCreditNotEnough || isCreditCardRequiredMessage?.length > 0) && (
                                <ErrorMsg
                                    mt={4}
                                    backgroundColor='nearWhite'
                                    paddingX={4}
                                    paddingY={3}
                                    color={isCreditCardRequiredError ? 'red' : 'black'}
                                    borderRadius={2}
                                    display='flex'
                                    alignItems='center'
                                    fontSize='base'
                                    role='alert'
                                    aria-live='polite'
                                >
                                    <Icon
                                        name='alert'
                                        color={isCreditCardRequiredError ? 'red' : 'gray'}
                                        size={16}
                                        marginRight={2}
                                        aria-hidden='true'
                                    />
                                    <span>{isCreditCardRequiredError ? isCreditCardRequiredMessage : locales.storeCreditNotEnoughMessage}</span>
                                </ErrorMsg>
                            ))}

                        <fieldset>
                            <Box
                                is='legend'
                                css={screenReaderOnlyStyle}
                            >
                                {locales.paymentMethodSelection}
                            </Box>
                            {paymentOptions?.creditCards?.length > 0 && (
                                <CreditCardsList
                                    cards={paymentOptions?.creditCards}
                                    isZeroCheckout={isZeroCheckout}
                                    isBopis={isBopis}
                                    setNewCreditCard={this.setNewCreditCard}
                                    showEditCreditCardForm={this.showEditCreditCardForm}
                                    updateSecurityCode={this.updateSecurityCode}
                                    shouldDisplayAddOrDeleteCreditCardButton={!zeroDollarHigherRedeemPointsCVV}
                                    defaultPayment={paymentOptions?.defaultPayment}
                                    securityCode={securityCode}
                                    securityCodeInvalid={securityCodeInvalid}
                                    selectedCreditCardId={this.state.selectedCreditCardId}
                                    creditCardPaymentGroup={creditCardPaymentGroup}
                                    creditCardOptions={paymentOptions?.creditCards}
                                    showEditButtonFirst={true}
                                    onUseThisCard={this.saveAndContinueDebounce}
                                    onRef={comp => (this.creditCardsList = comp)}
                                    alternateMethodSelected={this.props.alternateMethodSelected}
                                />
                            )}

                            {storeCredits?.length > 0 &&
                                storeCredits.map((storeCredit, index) => (
                                    <AccountCredit
                                        key={`storeCredits_${index.toString()}`}
                                        storeCredit={storeCredit}
                                        locales={locales}
                                    />
                                ))}

                            {/* Display existing gift cards */}
                            <AppliedGiftCards />

                            {shouldDisplayAddOrDeleteCreditCardButton && <AddCreditCardButton onClick={this.toggleOpenCreditCardForm()} />}

                            {/*GIFT CARD SECTION: TODO: maybe cab be removed later and used GiftCard from NewUserPaymentView*/}
                            <Divider marginY={[3, 4]} />
                            {this.renderGiftCard()}
                            <Divider marginY={[3, 4]} />

                            {!Sephora.isAgent && !isZeroCheckout && (
                                <>
                                    {/*Use NewUserPaymentView where Other Ways to Pay section with alternate buttons implemented */}
                                    <NewUserPaymentView
                                        localization={paymentLocales}
                                        availablePaymentMethods={{
                                            isPayPalEnabled,
                                            isVenmoEnabledForThisOrder,
                                            isKlarnaEnabledForThisOrder,
                                            isAfterpayEnabledForThisOrder,
                                            isAfterpayEnabledForThisProfile,
                                            isPazeEnabledForThisOrder,
                                            isPayPalPayLaterEligible
                                        }}
                                        orderDetails={orderDetails}
                                        installmentValue={installmentValue}
                                        disableContainerPadding
                                        withGiftCard={false}
                                        handlePayWithApplePayClick={this.handlePayWithApplePayClick}
                                        handlePayPalClick={this.handlePayPalPillClick}
                                        handleKlarnaExpand={this.handleKlarnaExpand}
                                        handleAfterPayExpand={this.handleAfterPayExpand}
                                        handleVenmoExpand={this.handleVenmoExpand}
                                        handlePazeExpand={this.handlePazeExpand}
                                    />
                                </>
                            )}
                        </fieldset>
                    </>
                )}
            </>
        );
    }
}

export default wrapComponent(PaymentMethodList, 'PaymentMethodList', true);
